/**
 * Geocoding Utilities — Nominatim (OpenStreetMap)
 *
 * Free geocoding without API keys.
 * Rate limit: 1 req/sec — add delays between batch calls.
 *
 * To switch to Google Maps Geocoding API see:
 *   docs/GOOGLE_MAPS_SETUP.md
 */

export interface GeocodedAddress {
  address: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
}

const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org';
const USER_AGENT = 'Hummii/1.0 (hummii.ca)';

// Nominatim returns full province names; map to 2-letter codes used in the app
const PROVINCE_CODES: Record<string, string> = {
  Alberta: 'AB',
  'British Columbia': 'BC',
  Manitoba: 'MB',
  'New Brunswick': 'NB',
  'Newfoundland and Labrador': 'NL',
  'Northwest Territories': 'NT',
  'Nova Scotia': 'NS',
  Nunavut: 'NU',
  Ontario: 'ON',
  'Prince Edward Island': 'PE',
  Quebec: 'QC',
  Saskatchewan: 'SK',
  Yukon: 'YT',
};

interface NominatimAddress {
  house_number?: string;
  road?: string;
  city?: string;
  town?: string;
  village?: string;
  municipality?: string;
  state?: string;
  postcode?: string;
  country?: string;
}

function parseNominatimAddress(addr: NominatimAddress): GeocodedAddress {
  const street = [addr.house_number, addr.road].filter(Boolean).join(' ');
  const city = addr.city || addr.town || addr.village || addr.municipality || '';
  const rawProvince = addr.state || '';
  const province = PROVINCE_CODES[rawProvince] || rawProvince;

  const rawPostal = (addr.postcode || '').toUpperCase().replace(/\s/g, '');
  const postalCode = rawPostal.length === 6 ? `${rawPostal.slice(0, 3)} ${rawPostal.slice(3)}` : rawPostal;

  return {
    address: street,
    city,
    province,
    postalCode,
    country: addr.country || 'Canada',
  };
}

/**
 * Reverse geocode coordinates → address details
 * Uses Nominatim (OpenStreetMap) — no API key required
 */
export async function reverseGeocode(
  latitude: number,
  longitude: number
): Promise<GeocodedAddress | null> {
  try {
    const params = new URLSearchParams({
      lat: latitude.toString(),
      lon: longitude.toString(),
      format: 'json',
      addressdetails: '1',
      'accept-language': 'en',
    });

    const res = await fetch(`${NOMINATIM_BASE}/reverse?${params}`, {
      headers: { 'User-Agent': USER_AGENT },
    });

    if (!res.ok) {
      console.error('[Geocoding] Nominatim reverse error:', res.status);
      return null;
    }

    const data: { address?: NominatimAddress; error?: string } = await res.json();

    if (data.error || !data.address) {
      console.warn('[Geocoding] No address found for coordinates:', latitude, longitude);
      return null;
    }

    return parseNominatimAddress(data.address);
  } catch (err) {
    console.error('[Geocoding] reverseGeocode failed:', err);
    return null;
  }
}

/**
 * Forward geocode address string → coordinates
 * Uses Nominatim (OpenStreetMap) — no API key required
 */
export async function forwardGeocode(
  address: string
): Promise<{ latitude: number; longitude: number } | null> {
  if (!address.trim()) return null;

  try {
    const params = new URLSearchParams({
      q: address,
      format: 'json',
      limit: '1',
      countrycodes: 'ca',
      'accept-language': 'en',
    });

    const res = await fetch(`${NOMINATIM_BASE}/search?${params}`, {
      headers: { 'User-Agent': USER_AGENT },
    });

    if (!res.ok) {
      console.error('[Geocoding] Nominatim forward error:', res.status);
      return null;
    }

    const data: Array<{ lat: string; lon: string }> = await res.json();

    if (!data.length) {
      console.warn('[Geocoding] No results for address:', address);
      return null;
    }

    return {
      latitude: parseFloat(data[0].lat),
      longitude: parseFloat(data[0].lon),
    };
  } catch (err) {
    console.error('[Geocoding] forwardGeocode failed:', err);
    return null;
  }
}

/**
 * Build full address string from components
 */
export function buildFullAddress(data: Partial<GeocodedAddress>): string {
  return [data.address, data.city, data.province, data.postalCode, data.country]
    .filter(Boolean)
    .join(', ');
}
